import React, { createContext, useContext, useState, ReactNode } from 'react';
import { VaultStatusFilter } from 'structure/pv2/dynavault/types';

type SortType = 'asc' | 'desc' | 'default';
export type Sort = {
  name: string;
  direction: SortType;
} | null;

type ViewType = 'table' | 'card';

interface VaultListContextType {
  vaultStatusFilter: VaultStatusFilter;
  setVaultStatusFilter: (vaultStatusFilter: VaultStatusFilter) => void;
  getSortDirection: (name: string) => SortType;
  changeSortDirection: (name: string) => void;
  sort: Sort;
  viewType: ViewType;
  setViewType: (viewType: ViewType) => void;
}

const VaultListContext = createContext<VaultListContextType | undefined>(
  undefined
);

interface VaultListProviderProps {
  children: ReactNode;
}

export const VaultListProvider: React.FC<VaultListProviderProps> = ({
  children,
}) => {
  const [sort, setSort] = useState<Sort>(null);
  const [viewType, setViewType] = useState<ViewType>('table');
  const [vaultStatusFilter, setVaultStatusFilter] =
    useState<VaultStatusFilter>('open');

  const changeSortDirection = (name: string) => {
    setSort((prevState) => {
      let newDirection: 'asc' | 'desc' | 'default';

      if (!prevState || prevState.name !== name) {
        newDirection = 'desc';
        return { name, direction: newDirection };
      }

      if (prevState.direction === 'default') {
        newDirection = 'desc';
      } else if (prevState.direction === 'desc') {
        newDirection = 'asc';
      } else {
        newDirection = 'default';
      }

      return { name, direction: newDirection };
    });
  };

  const getSortDirection = (name: string): SortType => {
    if (sort) {
      return sort.name === name ? sort.direction : 'default';
    }

    return 'default';
  };

  return (
    <VaultListContext.Provider
      value={{
        getSortDirection,
        changeSortDirection,
        sort,
        viewType,
        setViewType,
        vaultStatusFilter,
        setVaultStatusFilter,
      }}
    >
      {children}
    </VaultListContext.Provider>
  );
};

export const useVaultListContext = (): VaultListContextType => {
  const context = useContext(VaultListContext);
  if (context === undefined) {
    throw new Error('useVaultList must be used within a VaultListProvider');
  }
  return context;
};
